// ============================================================================
// useRovingTabindex (E4a / Paket D) — geteiltes Roving-Tabindex-Muster.
// ----------------------------------------------------------------------------
// docs/30 §9.4: Eine Karten-/Nav-Liste ist genau EIN Tab-Stop. Der CONTAINER
// traegt tabindex=0 + eine Rolle (listbox/list/…) + aria-activedescendant, das
// auf die id des aktiven Eintrags zeigt. JEDER Eintrag bekommt tabindex=-1 und
// eine stabile id. Pfeiltasten bewegen NUR den aktiven Deskriptor (keine
// Navigation); Enter/Leertaste ruft den Oeffnen-Handler des aktiven Eintrags.
//
// Innere interaktive Elemente eines Eintrags (⋯-Button, Favoriten-Stern,
// Footer-Buttons) muessen vom Aufrufer auf tabindex=-1 gesetzt werden, damit
// der Container der einzige tabbare Knoten der Liste bleibt. Per Maus/Touch +
// Kontextmenue bleiben sie erreichbar.
//
// Der Hook ist bewusst „controlled-optional": ohne activeId-Prop haelt er den
// aktiven Eintrag selbst; mit activeId/onActiveChange kann ein Aufrufer ihn von
// aussen steuern (z. B. die Nav an die aktive Route binden).
// ============================================================================

import {
  useCallback,
  useMemo,
  useRef,
  useState,
  type KeyboardEvent as ReactKeyboardEvent,
} from 'react';

/** Ausrichtung der Liste — steuert, welche Pfeiltasten bewegen. */
export type RovingOrientation = 'vertical' | 'horizontal' | 'grid';

export interface UseRovingTabindexOptions {
  /** Stabile, DOM-reihenfolge-gleiche ids aller Eintraege. */
  itemIds: string[];
  /**
   * Oeffnen-Handler des aktiven Eintrags. Bekommt die id des aktiven Eintrags;
   * der Aufrufer mappt sie auf sein Datum und navigiert. Enter/Leertaste ruft
   * ihn — die Pfeiltasten NICHT.
   */
  onActivate: (id: string) => void;
  /** ARIA-Rolle des Containers. Default 'listbox'. */
  role?: 'listbox' | 'list' | 'menu' | 'grid' | 'toolbar' | 'tablist';
  /** ARIA-Label des Containers (bereits uebersetzt). */
  ariaLabel?: string;
  /**
   * Bewegungsachse. 'vertical' = nur ↑/↓, 'horizontal' = nur ←/→, 'grid' =
   * alle vier (linear, ohne Zeilen-/Spalten-Rechnung — reicht fuer die
   * flachen Karten-Grids). Default 'grid'.
   */
  orientation?: RovingOrientation;
  /**
   * Kontrollierte aktive id (optional). Wird sie gesetzt, ist der Hook
   * „controlled" und ruft onActiveChange statt intern zu speichern.
   */
  activeId?: string | null;
  /** Callback bei Wechsel des aktiven Deskriptors (nur controlled). */
  onActiveChange?: (id: string) => void;
  /** Ohne aktiven Eintrag standardmaessig den ersten waehlen. */
  fallbackToFirst?: boolean;
  /**
   * Wenn true (Default), springt Pfeil ueber das Ende hinaus zum Anfang zurueck
   * (und umgekehrt). Bei false stoppt die Bewegung an den Raendern.
   */
  wrap?: boolean;
  /**
   * Tastatur-Weg zum Kontextmenue (E4a-Verify): Shift+F10 bzw. die
   * ContextMenu-Taste rufen diesen Handler mit der aktiven id — der Aufrufer
   * oeffnet das ⋯-Menue des Eintrags. Ohne Handler bleiben die Tasten unberuehrt.
   */
  onContextMenuRequest?: (id: string) => void;
}

export interface RovingContainerProps {
  role: string;
  tabIndex: 0;
  'aria-label'?: string;
  'aria-activedescendant': string | undefined;
  onKeyDown: (event: ReactKeyboardEvent<HTMLElement>) => void;
  onFocus: () => void;
}

export interface RovingItemProps {
  id: string;
  tabIndex: -1;
}

export interface UseRovingTabindexResult {
  /** Auf den Listen-Container spreadbar. */
  containerProps: RovingContainerProps;
  /** Props je Eintrag (id + tabIndex=-1). */
  getItemProps: (id: string) => RovingItemProps;
  /** Aktuell aktive id (aria-activedescendant zeigt darauf). */
  activeId: string | undefined;
}

// ↑/←/Home = rueckwaerts/Anfang, ↓/→/End = vorwaerts/Ende.
const PREV_KEYS: Record<RovingOrientation, string[]> = {
  vertical: ['ArrowUp'],
  horizontal: ['ArrowLeft'],
  grid: ['ArrowUp', 'ArrowLeft'],
};
const NEXT_KEYS: Record<RovingOrientation, string[]> = {
  vertical: ['ArrowDown'],
  horizontal: ['ArrowRight'],
  grid: ['ArrowDown', 'ArrowRight'],
};

export function useRovingTabindex({
  itemIds,
  onActivate,
  role = 'listbox',
  ariaLabel,
  orientation = 'grid',
  activeId: controlledActiveId,
  onActiveChange,
  fallbackToFirst = true,
  wrap = true,
  onContextMenuRequest,
}: UseRovingTabindexOptions): UseRovingTabindexResult {
  const controlled = controlledActiveId !== undefined;

  // Uncontrolled-Fallback: intern gehaltener aktiver Deskriptor.
  const [internalActiveId, setInternalActiveId] = useState<string | null>(null);

  const activeId = useMemo<string | undefined>(() => {
    if (itemIds.length === 0) return undefined;
    const candidate = controlled ? controlledActiveId : internalActiveId;
    if (candidate != null && itemIds.includes(candidate)) return candidate;
    return fallbackToFirst ? itemIds[0] : undefined;
  }, [controlled, controlledActiveId, fallbackToFirst, internalActiveId, itemIds]);

  // Ref auf den aktuellen activeId, damit onKeyDown stabil bleiben kann.
  const activeIdRef = useRef<string | undefined>(activeId);
  activeIdRef.current = activeId;
  const itemIdsRef = useRef<string[]>(itemIds);
  itemIdsRef.current = itemIds;

  const moveActive = useCallback(
    (id: string) => {
      if (controlled) onActiveChange?.(id);
      else setInternalActiveId(id);
    },
    [controlled, onActiveChange],
  );

  const onKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLElement>) => {
      const ids = itemIdsRef.current;
      if (ids.length === 0) return;

      const key = event.key;
      const prevKeys = PREV_KEYS[orientation];
      const nextKeys = NEXT_KEYS[orientation];
      const current = activeIdRef.current;
      const currentIndex = current != null ? ids.indexOf(current) : -1;

      // Shift+F10 / ContextMenu-Taste: Kontextmenue des aktiven Eintrags —
      // sonst waeren die ⋯-Sekundaeraktionen in Roving-Listen zeiger-exklusiv.
      if (onContextMenuRequest && (key === 'ContextMenu' || (key === 'F10' && event.shiftKey))) {
        const target = current ?? (fallbackToFirst ? ids[0] : undefined);
        if (target != null) {
          event.preventDefault();
          onContextMenuRequest(target);
        }
        return;
      }

      if (key === 'Enter' || key === ' ' || key === 'Spacebar') {
        // Enter/Leertaste oeffnet den aktiven Eintrag — aber nur, wenn der
        // Fokus wirklich auf dem Container liegt (nicht auf einem inneren
        // Button/Link, der das Event selbst behandelt).
        if (event.target !== event.currentTarget) return;
        const target = current ?? (fallbackToFirst ? ids[0] : undefined);
        if (target != null) {
          event.preventDefault();
          onActivate(target);
        }
        return;
      }

      let nextIndex: number | null = null;
      if (prevKeys.includes(key)) {
        nextIndex = currentIndex <= 0 ? (wrap ? ids.length - 1 : 0) : currentIndex - 1;
      } else if (nextKeys.includes(key)) {
        nextIndex = currentIndex < 0
          ? 0
          : currentIndex >= ids.length - 1
            ? (wrap ? 0 : ids.length - 1)
            : currentIndex + 1;
      } else if (key === 'Home') {
        nextIndex = 0;
      } else if (key === 'End') {
        nextIndex = ids.length - 1;
      }

      if (nextIndex !== null) {
        event.preventDefault();
        const nextId = ids[nextIndex];
        if (nextId != null && nextId !== current) moveActive(nextId);
      }
    },
    [fallbackToFirst, moveActive, onActivate, onContextMenuRequest, orientation, wrap],
  );

  // Erst-Fokus auf den Container: falls noch kein aktiver Deskriptor existiert,
  // den ersten Eintrag markieren, damit aria-activedescendant sofort zeigt.
  const onFocus = useCallback(() => {
    if (controlled) return;
    if (activeIdRef.current == null && itemIdsRef.current.length > 0) {
      setInternalActiveId(itemIdsRef.current[0]);
    }
  }, [controlled]);

  const containerProps = useMemo<RovingContainerProps>(
    () => ({
      role,
      tabIndex: 0 as const,
      'aria-label': ariaLabel,
      'aria-activedescendant': activeId,
      onKeyDown,
      onFocus,
    }),
    [activeId, ariaLabel, onFocus, onKeyDown, role],
  );

  const getItemProps = useCallback(
    (id: string): RovingItemProps => ({ id, tabIndex: -1 as const }),
    [],
  );

  return { containerProps, getItemProps, activeId };
}
